import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, ShoppingCart, Users, FileText, TrendingUp, Layout, Settings, 
  Grid3x3, Tag, CreditCard, Scale, Mail, Palette, Sparkles, MessageSquare, 
  Truck, DollarSign, BookOpen, Store, Percent, AlertCircle, Shield, 
  ChevronDown, ChevronRight, Megaphone, Image as ImageIcon, Globe, 
  Bell, FileCheck, Receipt, Gift, Crown, Eye, Share2, Download, UserPlus, Power, UserCog, Mailbox, Upload, Printer, PackageOpen, HelpCircle
} from 'lucide-react';
import axiosInstance from '../../api/axios';
import { OutOfStockWidget } from '../../components/admin/OutOfStockWidget';
import { LaunchChecklistBanner } from '../../components/admin/LaunchChecklistBanner';
import { toast } from 'sonner';
import { formatEUR } from '../../utils/format';


/**
 * ContentIdeasWidget — Zeigt die offenen KI-Content-Vorschläge (Social Media)
 * direkt auf dem Admin-Dashboard. Prominente Platzierung ganz oben, damit
 * die Shop-Betreiberin ihre Content-To-do-Liste sofort sieht.
 */
const ContentIdeasWidget = () => {
  const [ideas, setIdeas] = React.useState([]);
  const [count, setCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);

  const loadIdeas = React.useCallback(async () => {
    try {
      const res = await axiosInstance.get('/admin/content-creator/list?status=saved');
      const items = Array.isArray(res.data) ? res.data : (res.data?.ideas || []);
      setIdeas(items.slice(0, 3));
      setCount(res.data?.count ?? items.length);
    } catch (e) {
      // Endpoint noch nicht verfügbar → still leave empty
    } finally {
      setLoading(false);
    }
  }, []);

  const markDone = async (idea) => {
    // Optimistisch aus der Liste entfernen
    setIdeas((prev) => prev.filter((i) => i.id !== idea.id));
    setCount((c) => Math.max(0, c - 1));
    try {
      await axiosInstance.post('/admin/content-creator/done', { idea });
      toast.success('✅ Idee als erledigt markiert');
    } catch (_e) {
      toast.error('Idee konnte nicht als erledigt markiert werden');
      loadIdeas();  // Serverstand neu laden bei Fehler
    }
  };

  React.useEffect(() => { loadIdeas(); }, [loadIdeas]);

  const generateFresh = async () => {
    try {
      setLoading(true);
      // /generate liefert 3 neue Vorschläge, /save speichert sie ins To-do
      const gen = await axiosInstance.get('/admin/content-creator/generate?count=3');
      const fresh = Array.isArray(gen.data) ? gen.data : (gen.data?.ideas || []);
      for (const idea of fresh) {
        await axiosInstance.post('/admin/content-creator/save', { idea }).catch(() => null);
      }
      await loadIdeas();
    } catch (_e) {
      setLoading(false);
    }
  };

  return (
    <div className="mb-5 bg-white border-2 border-[#A070D0] p-3 md:p-4" data-testid="content-ideas-widget">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center" style={{ backgroundColor: '#F8F0FF' }}>
            <Sparkles className="h-4 w-4" style={{ color: '#A070D0' }} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-[#A070D0] font-bold">Social Media · KI-To-do</p>
            <p className="text-sm text-[#8B7355]">
              {loading ? 'Lädt …' : count > 0 ? `${count} offene Content-Ideen` : 'Keine offenen Ideen'}
            </p>
          </div>
        </div>
        <Link
          to="/admin/content-creator"
          className="text-xs bg-[#A070D0] hover:bg-[#8250B0] text-white px-3 py-1.5 font-semibold uppercase tracking-wider"
          data-testid="content-ideas-open-btn"
        >
          Öffnen →
        </Link>
      </div>

      {!loading && ideas.length === 0 && (
        <div className="text-center py-4">
          <p className="text-xs text-[#8B7355] mb-2">Noch keine Ideen gespeichert.</p>
          <button
            type="button"
            onClick={generateFresh}
            className="text-xs bg-[#F8F0FF] hover:bg-[#EFD8FF] text-[#A070D0] border border-[#A070D0] px-3 py-1.5 font-semibold"
            data-testid="content-ideas-generate-btn"
          >
            ✨ 3 KI-Vorschläge holen
          </button>
        </div>
      )}

      {ideas.length > 0 && (
        <ul className="space-y-1.5" data-testid="content-ideas-list">
          {ideas.map((idea, i) => (
            <li key={idea.id || i} className="flex items-start gap-2 text-sm text-[#5D4E3C] border-l-2 border-[#E8DCC8] pl-2">
              <span className="shrink-0 text-[10px] font-bold text-[#A070D0] uppercase mt-0.5">
                {(idea.template_id || idea.format || 'Post').replace(/_/g, ' ').slice(0, 12)}
              </span>
              <span className="line-clamp-2 flex-1">
                {idea.caption || idea.text || idea.title || idea.body || 'Content-Idee'}
              </span>
              <button
                type="button"
                onClick={() => markDone(idea)}
                className="shrink-0 inline-flex items-center gap-1 bg-[#E8F5E8] hover:bg-[#D0EBD0] active:bg-[#B8E1B8] text-[#3D6B3D] border border-[#A8D6A8] px-2 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors min-w-[44px] min-h-[28px] justify-center"
                title="Diese Idee als erledigt markieren"
                data-testid={`content-idea-done-${idea.id || i}`}
              >
                ✓ Fertig
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};


const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalProducts: 0,
    publishedProducts: 0,
    totalSubscribers: 0,
    totalCustomers: 0,
    totalVendors: 0,
    pendingVendors: 0,
    totalRevenue: 0,
    recentOrders: [],
    appDownloads: 0,
    newCustomersWeek: 0,
    newCustomersMonth: 0
  });
  const [loading, setLoading] = useState(true);
  const [topProducts, setTopProducts] = useState([]);
  const [topProductsPeriod, setTopProductsPeriod] = useState(30);
  const [slowMovers, setSlowMovers] = useState({ products: [], count: 0, tied_capital: 0 });
  const [slowMoversInfoOpen, setSlowMoversInfoOpen] = useState(false);
  const [revenueOverview, setRevenueOverview] = useState(null);
  const [revenuePeriod, setRevenuePeriod] = useState('month');
  const [expandedSections, setExpandedSections] = useState(() => {
    const saved = localStorage.getItem('dashboard_expanded_sections');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    fetchDashboardData();
  }, [topProductsPeriod, revenuePeriod]);

  useEffect(() => {
    localStorage.setItem('dashboard_expanded_sections', JSON.stringify(expandedSections));
  }, [expandedSections]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [ordersRes, productsRes, subscribersRes, customersRes, vendorsRes, statisticsRes, inboxRes, topProductsRes, slowMoversRes, revenueRes] = await Promise.all([
        axiosInstance.get('/orders/admin/all').catch(() => ({ data: { orders: [], statistics: {} } })),
        axiosInstance.get('/products?active_only=false').catch(() => ({ data: [] })),
        axiosInstance.get('/newsletter/subscribers').catch(() => ({ data: [] })),
        axiosInstance.get('/admin/customers/').catch(() => ({ data: [] })),
        axiosInstance.get('/vendors/admin/all').catch(() => ({ data: [] })),
        axiosInstance.get('/settings/statistics').catch(() => ({ data: { customers: {}, app_downloads: 0 } })),
        axiosInstance.get('/admin-inbox/unread-count').catch(() => ({ data: { unread_count: 0 } })),
        axiosInstance.get(`/orders/admin/top-products?days=${topProductsPeriod}&limit=5`).catch(() => ({ data: { products: [] } })),
        axiosInstance.get('/orders/admin/slow-movers?days=90&min_stock=3&max_sold=2&limit=5').catch(() => ({ data: { products: [], count: 0, tied_capital: 0 } })),
        axiosInstance.get(`/orders/admin/revenue-overview?period=${revenuePeriod}`).catch(() => ({ data: null })),
      ]);

      const vendors = vendorsRes.data || [];
      const ordersData = ordersRes.data;
      const orders = Array.isArray(ordersData) ? ordersData : (ordersData?.orders || []);
      const statsData = statisticsRes.data || {};
      
      setStats({
        totalOrders: orders.length,
        totalProducts: Array.isArray(productsRes.data) ? productsRes.data.length : 0,
        publishedProducts: Array.isArray(productsRes.data)
          ? productsRes.data.filter(p =>
              p.is_active !== false &&
              !p.is_draft &&
              (p.draft_status === undefined || p.draft_status === 'published')
            ).length
          : 0,
        totalSubscribers: Array.isArray(subscribersRes.data) ? subscribersRes.data.length : 0,
        totalCustomers: Array.isArray(customersRes.data) ? customersRes.data.length : 0,
        totalVendors: vendors.length,
        pendingVendors: vendors.filter(v => v.status === 'pending').length,
        totalRevenue: ordersData?.statistics?.total_revenue || 0,
        recentOrders: orders.slice(0, 5),
        appDownloads: statsData.app_downloads || 0,
        newCustomersWeek: statsData.customers?.last_7_days || 0,
        newCustomersMonth: statsData.customers?.last_30_days || 0
      });
      setTopProducts(topProductsRes.data?.products || []);
      setSlowMovers(slowMoversRes.data || { products: [], count: 0, tied_capital: 0 });
      setRevenueOverview(revenueRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  // Farbschema für konsistente Icon-Farben (braun-warm, abgestimmt auf #C4A084)
  const iconColors = {
    // Bestellungen/Shop - Sehr helles Pastell Orange
    orders: { bg: '#FFF8F0', text: '#E89860', border: '#FFEFE0' },
    // Produkte - Sehr helles Pastell Gelb
    products: { bg: '#FFFEF5', text: '#D4B030', border: '#FFF8D0' },
    // Kunden - Sehr helles Pastell Grün
    customers: { bg: '#F0FFF0', text: '#60B060', border: '#E0FFE0' },
    // Verkäufer - warmes Pastell Beige (passend zur Brand-Palette)
    vendors: { bg: '#FAF5EE', text: '#8B7355', border: '#E8DCC8' },
    // Newsletter/Mail - WARMES Braun statt Rosa (User-Request: E-Mail-Button war einziger rosa)
    mail: { bg: '#F5EDE4', text: '#8B6F4D', border: '#E8DCC8' },
    // Marketing - klares Pastell Blau (Marketing-Bereich soll sich farblich abheben)
    marketing: { bg: '#F0F8FF', text: '#60A0D0', border: '#E0F0FF' },
    // Umsatz/Geld - Sehr helles Pastell Grün
    revenue: { bg: '#F0FFF0', text: '#60B060', border: '#E0FFE0' },
    // Design/Palette - Sehr helles Pastell Lila
    design: { bg: '#F8F0FF', text: '#A070D0', border: '#F0E0FF' },
    // Einstellungen - Sehr helles Pastell Beige
    settings: { bg: '#FAF8F5', text: '#A09080', border: '#F0EDE8' },
  };

  // 3 Hauptgruppen mit Unterkategorien
  const mainGroups = [
    {
      id: 'shop-artikel',
      title: 'Shop & Artikel',
      icon: ShoppingCart,
      color: iconColors.orders,
      categories: [
        {
          id: 'shop',
          title: 'Shop verwalten',
          icon: ShoppingCart,
          color: iconColors.orders,
          items: [
            { to: '/admin/orders', icon: ShoppingCart, label: 'Bestellungen', badge: stats.totalOrders, color: iconColors.orders },
            { to: '/admin/abandoned-carts', icon: ShoppingCart, label: 'Verlassene Warenkörbe', color: iconColors.orders },
            { to: '/admin/customers', icon: Users, label: 'Kunden', badge: stats.totalCustomers, color: iconColors.orders },
            { to: '/admin/invoices', icon: FileText, label: 'Rechnungen', color: iconColors.orders },
            { to: '/admin/documents', icon: FileCheck, label: 'Dokumente', color: iconColors.orders },
          ]
        },
        {
          id: 'products',
          title: 'Produkte & Kategorien',
          icon: Package,
          color: iconColors.products,
          items: [
            { to: '/admin/products', icon: Package, label: 'Produkte', badge: stats.totalProducts, color: iconColors.products },
            { to: '/admin/product-drafts', icon: Upload, label: 'Artikel vorbereiten', color: iconColors.products },
            { to: '/admin/product-image-quality', icon: ImageIcon, label: 'Bildqualität prüfen', color: iconColors.products },
            { to: '/admin/label-composer', icon: Printer, label: 'Etiketten drucken', color: iconColors.products },
            { to: '/admin/returns', icon: PackageOpen, label: 'Retouren', color: iconColors.orders },
            { to: '/admin/inventory', icon: Package, label: 'Inventar & Lagerbestand', color: iconColors.products },
            { to: '/admin/brands', icon: Sparkles, label: 'Marken-Welten', color: iconColors.products },
            { to: '/admin/hair-collections', icon: Crown, label: 'Kollektionen', color: iconColors.products },
            { to: '/admin/categories', icon: Grid3x3, label: 'Kategorien', color: iconColors.products },
            { to: '/admin/discount-codes', icon: Tag, label: 'Rabattcodes', color: iconColors.products },
            { to: '/admin/catalogs', icon: BookOpen, label: 'PDF hochladen', color: iconColors.products },
            { to: '/admin/product-catalog-generator', icon: FileText, label: 'Katalog erstellen', color: iconColors.products },
          ]
        }
      ]
    },
    {
      id: 'verkaeufer-marketing',
      title: 'Verkäufer & Marketing',
      icon: Store,
      color: iconColors.vendors,
      categories: [
        {
          id: 'vendors',
          title: 'Verkäufer & Marktplatz',
          icon: Store,
          color: iconColors.vendors,
          items: [
            { to: '/admin/vendors', icon: Store, label: 'Verkäufer', badge: stats.totalVendors, color: iconColors.vendors },
            { to: '/admin/vendor-features', icon: Crown, label: 'Verkäufer-Abos', color: iconColors.vendors },
            { to: '/admin/vendor-settings', icon: Percent, label: 'Provisionen', color: iconColors.vendors },
            { to: '/admin/commission-tracking', icon: DollarSign, label: 'Provision-Tracking', color: iconColors.vendors },
            { to: '/admin/marketplace-settings', icon: Globe, label: 'Marktplatz-Modus', color: iconColors.vendors },
          ]
        },
        {
          id: 'marketing',
          title: 'Marketing & Newsletter',
          icon: Mail,
          color: iconColors.marketing,
          items: [
            { to: '/admin/postfach', icon: Mailbox, label: 'Postfach', badge: stats.unreadInbox, badgeAlert: true, color: iconColors.marketing },
            { to: '/admin/content-creator', icon: Sparkles, label: 'Content-Ideen (Social Media)', color: iconColors.marketing },
            { to: '/admin/content-studio', icon: Sparkles, label: 'Content-Studio (KI)', color: iconColors.marketing },
            { to: '/admin/catalog-pdf', icon: FileText, label: 'Katalog-PDF', color: iconColors.marketing },
            { to: '/admin/newsletter-builder', icon: Mail, label: 'Newsletter Builder', color: iconColors.marketing },
            { to: '/admin/campaigns', icon: Megaphone, label: 'Kampagnen', color: iconColors.marketing },
            { to: '/admin/benefits', icon: Gift, label: 'Mitglieder-Vorteile', color: iconColors.marketing },
            { to: '/admin/feedback', icon: MessageSquare, label: 'Feedback', color: iconColors.marketing },
            { to: '/admin/push-notifications', icon: Bell, label: 'Push-Benachr.', color: iconColors.marketing },
          ]
        }
      ]
    },
    {
      id: 'design-einstellungen',
      title: 'Design & Einstellungen',
      icon: Palette,
      color: iconColors.design,
      categories: [
        {
          id: 'design',
          title: 'Design & Seiten',
          icon: Palette,
          color: iconColors.design,
          items: [
            { to: '/admin/visual-page-editor', icon: Layout, label: 'Page Editor', color: iconColors.design },
            { to: '/admin/design', icon: Palette, label: 'Design', color: iconColors.design },
            { to: '/admin/seasonal-themes', icon: Sparkles, label: 'Saisonale Themes', color: iconColors.design },
            { to: '/admin/blog', icon: BookOpen, label: 'Blog', color: iconColors.design },
            { to: '/admin/blog-themen', icon: Tag, label: 'Themen-Glossar', color: iconColors.design },
            { to: '/admin/blog-questions', icon: MessageSquare, label: 'Leserfragen', color: iconColors.design },
            { to: '/admin/legal-pages', icon: Scale, label: 'Rechtliches', color: iconColors.design },
            { to: '/admin/faq', icon: HelpCircle, label: 'FAQ', color: iconColors.design },
          ]
        },
        {
          id: 'settings',
          title: 'Einstellungen',
          icon: Settings,
          color: iconColors.settings,
          items: [
            { to: '/admin/payment-settings', icon: CreditCard, label: 'Zahlungen', color: iconColors.settings },
            { to: '/admin/shipping-settings', icon: Truck, label: 'Versand', color: iconColors.settings },
            { to: '/admin/shipping-zones', icon: Globe, label: 'Auslandsversand (Vorbereitung)', color: iconColors.settings },
            { to: '/admin/email-settings', icon: Mail, label: 'E-Mail', color: iconColors.mail },
            { to: '/admin/admin-management', icon: Shield, label: 'Admins', color: iconColors.settings },
            { to: '/admin/profil', icon: UserCog, label: 'Mein Profil', color: iconColors.settings },
            { to: '/admin/app-icon-settings', icon: ImageIcon, label: 'App Icon', color: iconColors.settings },
            { to: '/admin/social-media', icon: Share2, label: 'Social Media', color: iconColors.settings },
            { to: '/admin/launch-checklist', icon: Power, label: 'Start-Checkliste', color: iconColors.settings },
            { to: '/admin/maintenance', icon: Power, label: 'Wartungsmodus', color: iconColors.settings },
            { to: '/admin/test-email', icon: Mail, label: 'Test-E-Mail', color: iconColors.settings },
            { to: '/admin/handbuch', icon: BookOpen, label: 'Handbuch', color: iconColors.settings },
          ]
        }
      ]
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#F5EDE4' }}>
        <p className="text-[#8B7355]">Lädt...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 px-3" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <div className="mb-5">
          <h1 className="text-xl font-bold text-[#5D4E3C]">Admin Dashboard</h1>
          <p className="text-sm text-[#8B7355]">Willkommen zurück!</p>
        </div>

        <LaunchChecklistBanner />
        <OutOfStockWidget />

        {/* Social-Media Content-Ideen-Widget — deine offene To-do-Liste immer sichtbar. */}
        <ContentIdeasWidget />

        {/* Statistik-Karten - Klickbar, mit Farbleiste zur klaren Unterscheidung */}
        <div className="grid grid-cols-3 gap-2 mb-5">
          <Link to="/admin/orders" className="group" data-testid="dashboard-stat-orders">
            <div className="bg-white p-3 relative border border-[#C4A084] hover:border-[#5D4E3C] hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: iconColors.orders.text }} />
              <div className="pl-1.5">
                <div className="w-8 h-8 flex items-center justify-center mb-2" style={{ backgroundColor: iconColors.orders.bg }}>
                  <ShoppingCart className="h-4 w-4" style={{ color: iconColors.orders.text }} />
                </div>
                <p className="text-xl font-bold text-[#5D4E3C] group-hover:text-[#3D3327] transition-colors">{stats.totalOrders}</p>
                <p className="text-[10px] text-[#8B7355]">Bestellungen</p>
              </div>
            </div>
          </Link>

          <Link to="/admin/products" className="group" data-testid="dashboard-stat-products">
            <div className="bg-white p-3 relative border border-[#C4A084] hover:border-[#5D4E3C] hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: iconColors.products.text }} />
              <div className="pl-1.5">
                <div className="w-8 h-8 flex items-center justify-center mb-2" style={{ backgroundColor: iconColors.products.bg }}>
                  <Package className="h-4 w-4" style={{ color: iconColors.products.text }} />
                </div>
                <p className="text-xl font-bold text-[#5D4E3C] group-hover:text-[#3D3327] transition-colors">{stats.totalProducts}</p>
                <p className="text-[10px] text-[#8B7355]">
                  Produkte
                  {stats.totalProducts > 0 && (
                    <span className="block text-[9px] text-[#A89073]">
                      davon {stats.publishedProducts} veröffentlicht
                    </span>
                  )}
                </p>
              </div>
            </div>
          </Link>

          <Link to="/admin/customers" className="group" data-testid="dashboard-stat-customers">
            <div className="bg-white p-3 relative border border-[#C4A084] hover:border-[#5D4E3C] hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-sky-500" />
              <div className="pl-1.5">
                <div className="w-8 h-8 flex items-center justify-center mb-2 bg-sky-100">
                  <Users className="h-4 w-4 text-sky-600" />
                </div>
                <p className="text-xl font-bold text-[#5D4E3C] group-hover:text-[#3D3327] transition-colors">{stats.totalCustomers}</p>
                <p className="text-[10px] text-[#8B7355]">Kunden</p>
              </div>
            </div>
          </Link>

          <Link to="/admin/vendors" className="group" data-testid="dashboard-stat-vendors">
            <div className="bg-white p-3 relative border border-[#C4A084] hover:border-[#5D4E3C] hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: iconColors.vendors.text }} />
              <div className="pl-1.5">
                <div className="w-8 h-8 flex items-center justify-center mb-2" style={{ backgroundColor: iconColors.vendors.bg }}>
                  <Store className="h-4 w-4" style={{ color: iconColors.vendors.text }} />
                </div>
                <p className="text-xl font-bold text-[#5D4E3C] group-hover:text-[#3D3327] transition-colors">{stats.totalVendors}</p>
                <p className="text-[10px] text-[#8B7355]">Verkäufer</p>
              </div>
            </div>
          </Link>

          <Link to="/admin/newsletter-settings" className="group" data-testid="dashboard-stat-newsletter">
            <div className="bg-white p-3 relative border border-[#C4A084] hover:border-[#5D4E3C] hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: iconColors.mail.text }} />
              <div className="pl-1.5">
                <div className="w-8 h-8 flex items-center justify-center mb-2" style={{ backgroundColor: iconColors.mail.bg }}>
                  <Mail className="h-4 w-4" style={{ color: iconColors.mail.text }} />
                </div>
                <p className="text-xl font-bold text-[#5D4E3C] group-hover:text-[#3D3327] transition-colors">{stats.totalSubscribers}</p>
                <p className="text-[10px] text-[#8B7355]">Newsletter</p>
              </div>
            </div>
          </Link>

          <Link to="/admin/orders" className="group" data-testid="dashboard-stat-revenue">
            <div className="bg-white p-3 relative border border-[#C4A084] hover:border-[#5D4E3C] hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500" />
              <div className="pl-1.5">
                <div className="w-8 h-8 flex items-center justify-center mb-2" style={{ backgroundColor: iconColors.revenue.bg }}>
                  <TrendingUp className="h-4 w-4" style={{ color: iconColors.revenue.text }} />
                </div>
                <p className="text-xl font-bold text-[#5D4E3C] group-hover:text-[#3D3327] transition-colors">{stats.totalRevenue.toFixed(0)}€</p>
                <p className="text-[10px] text-[#8B7355]">Umsatz</p>
              </div>
            </div>
          </Link>
        </div>

        {/* 📈 Verkaufs-Übersicht Widget — mit Zeitraum-Auswahl */}
        <div className="bg-white p-4 mb-5" style={{ border: '1px solid #C4A084' }} data-testid="revenue-overview-widget">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-[#5D4E3C] flex items-center gap-2">
              📈 Verkaufs-Übersicht
            </h3>
            <div className="flex gap-1" data-testid="revenue-period-tabs">
              {[
                { key: 'week', label: 'Woche' },
                { key: 'month', label: 'Monat' },
                { key: 'quarter', label: '3M' },
                { key: 'year', label: 'Jahr' },
              ].map(p => (
                <button
                  key={p.key}
                  onClick={() => setRevenuePeriod(p.key)}
                  className={`px-2 py-1 text-[11px] rounded-none border transition-colors ${
                    revenuePeriod === p.key
                      ? 'bg-[#8B6F4D] text-white border-[#8B6F4D]'
                      : 'bg-white text-[#5D4E3C] border-[#D4C4B0] hover:bg-[#FAF5EE]'
                  }`}
                  data-testid={`revenue-period-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {!revenueOverview ? (
            <p className="text-xs text-[#8B7355] italic py-4 text-center">Lade Daten...</p>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: 'Umsatz', value: `${formatEUR(revenueOverview.current.revenue)}`, trend: revenueOverview.trend.revenue_pct, prev: `${formatEUR(revenueOverview.previous.revenue)}` },
                  { label: 'Bestellungen', value: `${revenueOverview.current.orders}`, trend: revenueOverview.trend.orders_pct, prev: `${revenueOverview.previous.orders}` },
                  { label: 'Ø Bestellwert', value: `${formatEUR(revenueOverview.current.avg_order)}`, trend: revenueOverview.trend.avg_order_pct, prev: `${formatEUR(revenueOverview.previous.avg_order)}` },
                ].map((m, i) => {
                  const up = m.trend > 0;
                  const flat = m.trend === 0;
                  return (
                    <div key={i} className="text-center bg-[#FAF5EE] border border-[#EFE5D7] p-2">
                      <p className="text-[10px] text-[#8B7355] uppercase tracking-wide">{m.label}</p>
                      <p className="text-base font-bold text-[#5D4E3C] mt-0.5">{m.value}</p>
                      <p className={`text-[10px] mt-0.5 ${flat ? 'text-gray-400' : up ? 'text-green-700' : 'text-red-700'}`}>
                        {flat ? '━' : up ? '↑' : '↓'} {Math.abs(m.trend)}% vs. Vor{revenuePeriod === 'week' ? 'woche' : revenuePeriod === 'month' ? 'monat' : revenuePeriod === 'quarter' ? 'quartal' : 'jahr'}
                      </p>
                      <p className="text-[9px] text-[#A99B85] mt-0.5">war: {m.prev}</p>
                    </div>
                  );
                })}
              </div>

              {revenueOverview.timeline.length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] text-[#8B7355] mb-1">
                    Verlauf ({revenueOverview.timeline_group_by === 'day' ? 'täglich' : 'wöchentlich'}):
                  </p>
                  <div className="flex items-end gap-0.5 h-16 bg-[#FAF5EE] px-1 pb-1 border border-[#EFE5D7]">
                    {(() => {
                      const maxRev = Math.max(...revenueOverview.timeline.map(t => t.revenue), 1);
                      return revenueOverview.timeline.map((t, i) => (
                        <div
                          key={i}
                          className="flex-1 bg-[#8B6F4D] hover:bg-[#5D4E3C] transition-colors"
                          style={{ height: `${(t.revenue / maxRev) * 100}%`, minHeight: '2px' }}
                          title={`${t.bucket}: ${formatEUR(t.revenue)} · ${t.orders} Bestellung(en)`}
                        />
                      ));
                    })()}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Neue Statistiken - App Downloads & Kunden */}
        <div className="bg-white p-4 mb-5" style={{ border: '1px solid #C4A084' }} data-testid="growth-widget">
          <h3 className="font-semibold text-[#5D4E3C] mb-3 flex items-center gap-2">
            <TrendingUp className="h-4 w-4" /> Wachstum
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-2" style={{ backgroundColor: '#F5EDE4' }}>
              <div className="flex justify-center mb-1">
                <Download className="h-4 w-4 text-[#8B7355]" />
              </div>
              <p className="text-lg font-bold text-[#5D4E3C]">{stats.appDownloads}</p>
              <p className="text-[9px] text-[#8B7355]">App Downloads</p>
            </div>
            <div className="text-center p-2" style={{ backgroundColor: '#F5EDE4' }}>
              <div className="flex justify-center mb-1">
                <UserPlus className="h-4 w-4 text-[#8B7355]" />
              </div>
              <p className="text-lg font-bold text-[#5D4E3C]">{stats.newCustomersWeek}</p>
              <p className="text-[9px] text-[#8B7355]">Neue Kunden (7 Tage)</p>
            </div>
            <div className="text-center p-2" style={{ backgroundColor: '#F5EDE4' }}>
              <div className="flex justify-center mb-1">
                <Users className="h-4 w-4 text-[#8B7355]" />
              </div>
              <p className="text-lg font-bold text-[#5D4E3C]">{stats.newCustomersMonth}</p>
              <p className="text-[9px] text-[#8B7355]">Neue Kunden (30 Tage)</p>
            </div>
          </div>
        </div>

        {/* 🏆 Meistgekauft-Widget */}
        <div className="bg-white p-4 mb-5" style={{ border: '1px solid #C4A084' }} data-testid="top-products-widget">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-[#5D4E3C] flex items-center gap-2">
              🏆 Meistgekauft
            </h3>
            <select
              value={topProductsPeriod}
              onChange={(e) => setTopProductsPeriod(Number(e.target.value))}
              className="text-xs border border-[#D4C4B0] px-2 py-1 bg-[#FAF5EE] focus:outline-none focus:border-[#8B6F4D]"
              data-testid="top-products-period-select"
            >
              <option value={7}>Letzte 7 Tage</option>
              <option value={30}>Letzte 30 Tage</option>
              <option value={90}>Letzte 90 Tage</option>
              <option value={365}>Letztes Jahr</option>
            </select>
          </div>

          {topProducts.length === 0 ? (
            <p className="text-xs text-[#8B7355] py-4 text-center italic">
              Noch keine verkauften Produkte in diesem Zeitraum. Sobald erste Bestellungen eingehen, erscheinen hier deine Renner.
            </p>
          ) : (
            <div className="space-y-2">
              {(() => {
                const maxQty = Math.max(...topProducts.map(p => p.quantity), 1);
                return topProducts.map((p, i) => (
                  <div
                    key={p.product_id || i}
                    className="flex items-center gap-2 py-1.5"
                    data-testid={`top-product-${i}`}
                  >
                    <div className="w-6 flex-shrink-0 text-center">
                      <span className={`text-sm font-bold ${
                        i === 0 ? 'text-[#B8860B]' :
                        i === 1 ? 'text-[#808080]' :
                        i === 2 ? 'text-[#CD7F32]' :
                        'text-[#8B7355]'
                      }`}>
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                      </span>
                    </div>
                    <div className="w-10 h-10 flex-shrink-0 border border-[#D4C4B0] bg-[#FAF5EE] flex items-center justify-center overflow-hidden">
                      {p.image ? (
                        <img src={p.image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Package className="h-4 w-4 text-[#8B7355]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[#5D4E3C] truncate">
                        {p.product_name}
                        {!p.is_active && p.product_id && (
                          <span className="ml-1 text-[9px] px-1 py-0.5 bg-gray-100 text-gray-500 rounded">
                            inaktiv
                          </span>
                        )}
                      </p>
                      <div className="mt-0.5 h-1.5 bg-[#F5EDE4] overflow-hidden">
                        <div
                          className="h-full bg-[#8B6F4D]"
                          style={{ width: `${(p.quantity / maxQty) * 100}%` }}
                        />
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-[#5D4E3C]">{p.quantity}×</p>
                      <p className="text-[10px] text-[#8B7355]">{p.revenue.toFixed(0)}€</p>
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>

        {/* 🐢 Langsam-Dreher Widget */}
        <div className="bg-white p-4 mb-5" style={{ border: '1px solid #C4A084' }} data-testid="slow-movers-widget">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-semibold text-[#5D4E3C] flex items-center gap-2 flex-wrap">
              🐢 Langsam-Dreher
              <button
                type="button"
                onClick={() => setSlowMoversInfoOpen((v) => !v)}
                className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-[#C4A084] text-[10px] text-[#5D4E3C] hover:bg-[#F5EDE4] transition-colors"
                title="Was bedeutet das?"
                aria-label="Erklärung anzeigen"
                data-testid="slow-movers-info-btn"
              >
                ℹ
              </button>
              <span className="text-[10px] font-normal text-[#8B7355]">
                (≥3 auf Lager · ≤2 verkauft in 90 Tagen)
              </span>
            </h3>
            {slowMovers.tied_capital > 0 && (
              <span
                className="text-[10px] px-2 py-0.5 bg-amber-50 text-amber-800 border border-amber-200"
                title="Gebundenes Kapital (Bestand × Preis)"
              >
                💰 {slowMovers.tied_capital.toFixed(0)}€ gebunden
              </span>
            )}
          </div>

          {slowMoversInfoOpen && (
            <div
              className="mb-3 p-3 text-xs text-[#5D4E3C] bg-[#FAF5EF] border border-[#E8DCC8] leading-relaxed"
              data-testid="slow-movers-info-panel"
            >
              <p className="font-semibold mb-1.5">Was bedeutet {'„Langsam-Dreher"'}?</p>
              <p className="mb-2">
                🐢 <strong>Langsam-Dreher</strong> (auch {'„Ladenhüter"'}) sind Artikel, die im Lager liegen
                und selten verkauft werden. Konkret: mindestens <strong>3 Stück auf Lager</strong> und
                höchstens <strong>2 Verkäufe in den letzten 90 Tagen</strong>.
              </p>
              <p className="font-semibold mb-1.5">Was bedeutet {'„gebundenes Kapital"'}?</p>
              <p className="mb-2">
                💰 Das ist das Geld, das du beim Einkauf für diese Ware ausgegeben hast und das jetzt
                im Regal <strong>{'„festhängt"'}</strong> — statt in deiner Kasse zu sein. Verkaufst du
                diese Artikel, wird dieses Geld wieder <strong>flüssig</strong> und du kannst neue,
                schneller drehende Ware bestellen.
              </p>
              <p className="text-[#8B7355] italic">
                Tipp: eine Rabattaktion oder ein Newsletter mit diesen Artikeln hilft, den Bestand
                aufzulösen.
              </p>
            </div>
          )}

          {slowMovers.products.length === 0 ? (
            <p className="text-xs text-[#8B7355] py-4 text-center italic">
              Keine Ladenhüter gefunden — alle deine Produkte drehen sich gut! ✨
            </p>
          ) : (
            <>
              <div className="space-y-2">
                {slowMovers.products.map((p, i) => (
                  <div
                    key={p.product_id || i}
                    className="flex items-center gap-2 py-1.5 border-b border-[#F5EDE4] last:border-0"
                    data-testid={`slow-mover-${i}`}
                  >
                    <div className="w-10 h-10 flex-shrink-0 border border-[#D4C4B0] bg-[#FAF5EE] flex items-center justify-center overflow-hidden">
                      {p.image ? (
                        <img src={p.image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Package className="h-4 w-4 text-[#8B7355]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[#5D4E3C] truncate">
                        {p.product_name}
                      </p>
                      <p className="text-[10px] text-[#8B7355]">
                        {p.category || 'Sonstiges'} · {formatEUR(p.price)}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs">
                        <span className="font-bold text-[#5D4E3C]">{p.stock}</span>
                        <span className="text-[#8B7355]"> auf Lager</span>
                      </p>
                      <p className="text-[10px] text-amber-700">
                        nur {p.sold_in_period}× verkauft
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              {slowMovers.count > slowMovers.products.length && (
                <p className="text-[11px] text-[#8B7355] mt-2 text-center italic">
                  … und {slowMovers.count - slowMovers.products.length} weitere Ladenhüter (Top 5 gezeigt)
                </p>
              )}
              <div className="mt-3 p-2 bg-[#FFF8E6] border border-[#F0DFA0] text-[11px] text-[#8B7355]">
                💡 Tipp: Erstelle einen Rabattcode für diese Artikel und bewerbe sie im Newsletter — so wird gebundenes Kapital wieder flüssig.
              </div>
            </>
          )}
        </div>

        {/* 3 Hauptgruppen */}
        <div className="space-y-3">
          {mainGroups.map((group) => {
            const isGroupExpanded = expandedSections.includes(group.id);
            const GroupIcon = group.icon;
            
            return (
              <div key={group.id} className="bg-white" style={{ border: '1px solid #AF9774' }}>
                {/* Hauptgruppen-Header - Goldbraun */}
                <button
                  onClick={() => toggleSection(group.id)}
                  className="w-full flex items-center justify-between p-4 transition-colors"
                  style={{ backgroundColor: '#AF9774' }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 flex items-center justify-center rounded" style={{ backgroundColor: group.color.bg }}>
                      <GroupIcon className="h-6 w-6" style={{ color: group.color.text }} />
                    </div>
                    <h2 className="font-bold text-lg text-white">{group.title}</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    {isGroupExpanded ? (
                      <ChevronDown className="h-6 w-6 text-white" />
                    ) : (
                      <ChevronRight className="h-6 w-6 text-white" />
                    )}
                  </div>
                </button>

                {/* Unterkategorien */}
                {isGroupExpanded && (
                  <div className="p-3 bg-[#FDFBF8]" style={{ borderTop: '1px solid #C4A084' }}>
                    <div className="space-y-2">
                      {group.categories.map((category) => {
                        const isCategoryExpanded = expandedSections.includes(category.id);
                        const CategoryIcon = category.icon;
                        const catColor = category.color;
                        
                        return (
                          <div key={category.id} className="bg-white" style={{ border: '1px solid #C4A084' }}>
                            {/* Kategorie-Header */}
                            <button
                              onClick={() => toggleSection(category.id)}
                              className="w-full flex items-center justify-between p-3 transition-colors hover:bg-[#F5EDE4]"
                            >
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 flex items-center justify-center rounded" style={{ backgroundColor: catColor.bg }}>
                                  <CategoryIcon className="h-4 w-4" style={{ color: catColor.text }} />
                                </div>
                                <span className="font-semibold text-sm text-[#5D4E3C]">{category.title}</span>
                              </div>
                              {isCategoryExpanded ? (
                                <ChevronDown className="h-5 w-5 text-[#8B7355]" />
                              ) : (
                                <ChevronRight className="h-5 w-5 text-[#8B7355]" />
                              )}
                            </button>

                            {/* Items */}
                            {isCategoryExpanded && (
                              <div className="p-2 bg-[#FAF8F5]" style={{ borderTop: '1px solid #E8DFD4' }}>
                                <div className="grid grid-cols-2 gap-1.5">
                                  {category.items.map((item) => {
                                    const ItemIcon = item.icon;
                                    const itemColor = item.color || catColor;
                                    return (
                                      <Link key={item.to} to={item.to}>
                                        <div 
                                          className="bg-white transition-all p-2.5 flex items-center gap-2 hover:opacity-80 min-h-[44px]"
                                          style={{ border: '1px solid #C4A084' }}
                                          data-testid={`admin-dashboard-item-${item.to.replace(/\//g, '-')}`}
                                        >
                                          <div 
                                            className="w-6 h-6 flex items-center justify-center flex-shrink-0 rounded"
                                            style={{ backgroundColor: itemColor.bg }}
                                          >
                                            <ItemIcon className="h-3.5 w-3.5" style={{ color: itemColor.text }} />
                                          </div>
                                          <span className="text-[11px] sm:text-xs font-medium text-[#5D4E3C] leading-tight whitespace-normal break-words flex-1">{item.label}</span>
                                          {item.badge > 0 && (
                                            <span 
                                              className="ml-auto text-[10px] px-1.5 py-0.5 font-medium rounded flex-shrink-0"
                                              style={{ backgroundColor: itemColor.bg, color: itemColor.text }}
                                            >
                                              {item.badge}
                                            </span>
                                          )}
                                        </div>
                                      </Link>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
};

export default AdminDashboard;
